/**
 * Quiz Preview Map Component
 *
 * Displays a preview map of the quiz's geographic coverage on the stats page.
 * Loads and filters the GeoJSON collection based on quiz configuration.
 */

import { useEffect, useState } from "react";
import { BaseMap } from "@/components/shared/BaseMap";
import { Card, CardContent } from "@/components/ui/card";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import type { QuizConfig } from "@/db/types";
import type { PathOptions } from "leaflet";
import { fetchGeoJSONFromUrl, getFeatureIdentifier } from "@/utils/geojsonLoader";
import { applyFilters, filterArrayValues } from "@/utils/filterUtils";
import type { FilterRule } from "@/stores/useStudioStore";
import { autoMerge } from "@/utils/grassyJSON";

interface QuizPreviewMapProps {
  config: QuizConfig;
  className?: string;
  height?: string | number;
}

// Style for all features
const FEATURE_STYLE: PathOptions = {
  fillColor: "#3b82f6", // Blue-500
  color: "#ffffff",
  weight: 1,
  opacity: 0.8,
  fillOpacity: 0.4,
};

export function QuizPreviewMap({ config, className, height = "280px" }: QuizPreviewMapProps) {
  const [collection, setCollection] = useState<FeatureCollection<Geometry, GeoJsonProperties> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load and process the GeoJSON collection
  useEffect(() => {
    const loadCollection = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let rawCollection: FeatureCollection<Geometry, GeoJsonProperties>;

        if (config.storedCollection) {
          // Inline collection (uploaded file) - already filtered and saved
          rawCollection = JSON.parse(config.storedCollection);
        } else if (config.datasetSource?.type === "dataset-url") {
          // Load from URL - need to apply filters
          rawCollection = await fetchGeoJSONFromUrl(config.datasetSource.path);

          // Convert StoredFilterRule to FilterRule format (add id field)
          const filters: FilterRule[] = config.filters.map((f, index) => ({
            ...f,
            id: `filter-${index}`,
          }));

          // Apply filters to features (include/exclude entire features)
          let processedCollection = applyFilters(rawCollection, filters);

          // Apply filters to array values within features (CRITICAL FIX)
          // This ensures only matching array values generate quiz prompts
          processedCollection = filterArrayValues(
            processedCollection,
            filters,
            config.promptKeys
          );

          // Apply exclusions
          const excludedSet = new Set(config.excludedFeatureIds);
          processedCollection = {
            ...processedCollection,
            features: processedCollection.features.filter((feature, index) => {
              const featureId = getFeatureIdentifier(feature, index);
              return !excludedSet.has(featureId);
            }),
          };

          // Apply auto-merge based on prompt keys
          if (config.promptKeys.length > 0) {
            const comparisonFunc = (
              a: { properties?: GeoJsonProperties | null },
              b: { properties?: GeoJsonProperties | null }
            ): boolean => {
              if (!a.properties || !b.properties) return false;
              return config.promptKeys.every((key) => {
                const aValue = a.properties?.[key];
                const bValue = b.properties?.[key];
                const aStr = Array.isArray(aValue)
                  ? JSON.stringify([...aValue].sort())
                  : JSON.stringify(aValue);
                const bStr = Array.isArray(bValue)
                  ? JSON.stringify([...bValue].sort())
                  : JSON.stringify(bValue);
                return aStr === bStr;
              });
            };

            try {
              processedCollection = autoMerge(processedCollection, comparisonFunc, 200);
            } catch (error) {
              console.error("Auto-merge failed in quiz preview:", error);
              // Continue with non-merged collection
            }
          }

          rawCollection = processedCollection;
        } else {
          setError("Invalid quiz configuration: missing dataset source");
          setIsLoading(false);
          return;
        }

        setCollection(rawCollection);
        setIsLoading(false);
      } catch (err) {
        console.error("Failed to load quiz collection:", err);
        setError(err instanceof Error ? err.message : "Failed to load map");
        setIsLoading(false);
      }
    };

    loadCollection();
  }, [config]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-0 overflow-hidden rounded-lg">
          <div className="flex items-center justify-center bg-muted/30" style={{ height }}>
            <p className="text-sm text-muted-foreground">Loading map preview...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-0 overflow-hidden rounded-lg">
          <div className="flex items-center justify-center bg-muted/30" style={{ height }}>
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!collection) {
    return null;
  }

  return (
    <Card className={className}>
      <CardContent className="p-0 overflow-hidden rounded-lg">
        <BaseMap
          data={collection}
          mapHeight={height}
          getFeatureStyle={() => FEATURE_STYLE}
          mapStyle="voyager"
          hideLabels={false}
          initialZoom={1}
        />
      </CardContent>
    </Card>
  );
}
